import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { ThemePreference } from '../types'
import { useAuth } from './useAuth'

// A theme choice is the same shape as the persisted profile preference.
export type ThemeChoice = ThemePreference
export type ResolvedTheme = 'light' | 'dark'

interface ThemeContextValue {
  theme: ThemeChoice
  resolved: ResolvedTheme
  setTheme: (t: ThemeChoice) => void
}

const STORAGE_KEY = 'b40-theme'

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

function resolve(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? getSystemTheme() : choice
}

function applyTheme(resolved: ResolvedTheme) {
  document.documentElement.setAttribute('data-theme', resolved)
  const meta = document.getElementById('theme-color-meta') as HTMLMetaElement | null
  if (meta) meta.content = resolved === 'light' ? '#dddbd3' : '#121009'
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  resolved: 'dark',
  setTheme: () => {},
})

export function useThemeProvider() {
  const { user, profile, updateThemePreference } = useAuth()

  // localStorage is the anonymous default and the first-paint cache (also read
  // by the anti-flash script in public/index.html). The signed-in user's
  // profile is the source of truth and overrides this on sign-in.
  const [theme, setThemeState] = useState<ThemeChoice>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemeChoice | null
    return stored && ['light', 'dark', 'system'].includes(stored) ? stored : 'dark'
  })

  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(theme))

  // Tracks the profile id whose preference we've already applied, so the
  // profile-wins effect runs once per sign-in and doesn't clobber later
  // in-session changes.
  const appliedForProfile = useRef<string | null>(null)

  // Apply a choice locally: state + localStorage cache + DOM. Does not persist
  // to the profile (used both for user actions and for reflecting the profile).
  const applyChoice = useCallback((t: ThemeChoice) => {
    localStorage.setItem(STORAGE_KEY, t)
    setThemeState(t)
    const r = resolve(t)
    setResolved(r)
    applyTheme(r)
  }, [])

  const setTheme = useCallback((t: ThemeChoice) => {
    applyChoice(t)
    // Persist to the profile when signed in. Mark it as already-applied so the
    // profile-wins effect (which fires when the in-memory profile updates)
    // doesn't fight this fresh choice.
    if (user) {
      appliedForProfile.current = user.id
      void updateThemePreference(t)
    }
  }, [user, updateThemePreference, applyChoice])

  // Profile wins: once the signed-in user's profile loads, apply its stored
  // preference, overriding whatever this browser had cached. Runs once per
  // profile; resets on sign-out so the next sign-in re-syncs.
  useEffect(() => {
    if (!profile) {
      appliedForProfile.current = null
      return
    }
    if (appliedForProfile.current === profile.id) return
    appliedForProfile.current = profile.id
    if (profile.themePreference !== theme) {
      applyChoice(profile.themePreference)
    }
  }, [profile, theme, applyChoice])

  useEffect(() => {
    applyTheme(resolved)
  }, [resolved])

  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const handler = () => {
      const r = getSystemTheme()
      setResolved(r)
      applyTheme(r)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  return { theme, resolved, setTheme }
}

export function useTheme() {
  return useContext(ThemeContext)
}
